import React from "react";
import {
    Brain,
    Target,
    BatteryCharging,
    Handshake,
    ShieldCheck,
    Moon,
    TrendingUp,
    Scale,
    Infinity as InfinityIcon,
} from "lucide-react";
import {
    LOGO_URL,
    PHONE_DISPLAY,
    EMAIL,
    INSTAGRAM,
    waUrl,
} from "../lib/constants";

/**
 * 2-page A4 REAL ESTATE brochure for The Reset Sanctuary.
 * Page 1: Problem · Solution · 4 Benefits
 * Page 2: Success Support · Equipment · Tatva K · Packages · Contact
 * View at /brochure-realestate. Print → Save as PDF.
 * Compliant wording (no banned terms outside Berklee credential).
 */

const GOLD = "#c9a227";
const GOLD_LINE = "rgba(201,162,39,0.25)";
const GOLD_LINE_SOFT = "rgba(201,162,39,0.15)";

const PageFrame = ({ children, testid }) => (
    <article
        className="brochure-page brochure-re-page"
        data-testid={testid}
        style={{
            width: 794,
            minHeight: 1123,
            maxWidth: "100%",
            margin: "0 auto 32px",
            backgroundColor: "#0a0a0a",
            color: "#bbb",
            boxShadow:
                "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,162,39,0.18)",
            padding: "40px 44px 32px",
            boxSizing: "border-box",
            position: "relative",
            fontFamily: "Inter, sans-serif",
            display: "flex",
            flexDirection: "column",
        }}
    >
        <div
            style={{
                position: "absolute",
                inset: 18,
                border: "1px solid rgba(201,162,39,0.22)",
                pointerEvents: "none",
            }}
        />
        {children}
    </article>
);

const PageHeader = ({ kicker }) => (
    <header
        style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            marginBottom: 14,
        }}
    >
        <img src={LOGO_URL} alt="The Reset Sanctuary" style={{ height: 58 }} />
        <div style={{ textAlign: "right" }}>
            <p
                style={{
                    color: GOLD,
                    fontSize: 9,
                    letterSpacing: "0.35em",
                    textTransform: "uppercase",
                    margin: 0,
                    fontWeight: 500,
                }}
            >
                {kicker}
            </p>
            <p
                style={{
                    color: "#888",
                    fontSize: 8.5,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    margin: "4px 0 0",
                }}
            >
                Dubai · RAK
            </p>
        </div>
    </header>
);

const PageFooter = ({ pageNum, tagline }) => (
    <div
        style={{
            marginTop: "auto",
            paddingTop: 14,
            borderTop: `1px solid ${GOLD_LINE_SOFT}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            fontSize: 8,
            color: "#666",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            gap: 12,
        }}
    >
        <span>theresetsanctuary.org</span>
        <span style={{ color: GOLD, textAlign: "center", flex: 1 }}>
            {tagline}
        </span>
        <span>Page {pageNum} / 2</span>
    </div>
);

const SectionTitle = ({ kicker, title, color = "#fff" }) => (
    <div style={{ marginBottom: 10 }}>
        <p
            style={{
                color: GOLD,
                fontSize: 9,
                letterSpacing: "0.35em",
                textTransform: "uppercase",
                margin: "0 0 6px",
                fontWeight: 500,
            }}
        >
            {kicker}
        </p>
        <h2
            className="font-serif"
            style={{
                color,
                fontSize: 20,
                fontWeight: 500,
                margin: 0,
                lineHeight: 1.1,
                letterSpacing: "-0.005em",
            }}
        >
            {title}
        </h2>
    </div>
);

const BenefitTile = ({ Icon, label, body, testid }) => (
    <div
        data-testid={testid}
        style={{
            padding: "14px 13px",
            border: `1px solid ${GOLD_LINE}`,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            backgroundColor: "rgba(201,162,39,0.025)",
        }}
    >
        <Icon size={24} color={GOLD} strokeWidth={1.4} />
        <p
            style={{
                color: "#fff",
                fontSize: 10,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                margin: 0,
                fontWeight: 500,
                lineHeight: 1.25,
            }}
        >
            {label}
        </p>
        <p
            style={{
                color: "#aaa",
                fontSize: 8.5,
                margin: 0,
                lineHeight: 1.5,
                fontWeight: 300,
            }}
        >
            {body}
        </p>
    </div>
);

const SmallBenefit = ({ Icon, label, body, testid }) => (
    <div
        data-testid={testid}
        style={{
            padding: "12px 10px",
            border: `1px solid ${GOLD_LINE_SOFT}`,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            alignItems: "center",
            textAlign: "center",
        }}
    >
        <Icon size={22} color={GOLD} strokeWidth={1.4} />
        <p
            style={{
                color: "#fff",
                fontSize: 9,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                margin: 0,
                fontWeight: 500,
                lineHeight: 1.25,
            }}
        >
            {label}
        </p>
        <p
            style={{
                color: "#aaa",
                fontSize: 8.5,
                margin: 0,
                lineHeight: 1.5,
                fontWeight: 300,
            }}
        >
            {body}
        </p>
    </div>
);

const Pillar = ({ title, body }) => (
    <div style={{ padding: "12px 12px", borderTop: `1px solid ${GOLD_LINE}` }}>
        <p
            style={{
                color: GOLD,
                fontSize: 8.5,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                margin: "0 0 6px",
                fontWeight: 500,
                lineHeight: 1.3,
            }}
        >
            {title}
        </p>
        <p
            style={{
                color: "#bbb",
                fontSize: 9,
                lineHeight: 1.55,
                margin: 0,
                fontWeight: 300,
            }}
        >
            {body}
        </p>
    </div>
);

const Package = ({ tier, price, period, headline, items, featured }) => (
    <div
        style={{
            padding: "18px 16px 20px",
            border: featured ? `1px solid ${GOLD}` : `1px solid ${GOLD_LINE}`,
            backgroundColor: featured ? "rgba(201,162,39,0.04)" : "transparent",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            height: "100%",
        }}
        data-testid={`re-package-${tier.toLowerCase()}`}
    >
        {featured && (
            <span
                style={{
                    position: "absolute",
                    top: -10,
                    left: 14,
                    backgroundColor: GOLD,
                    color: "#0a0a0a",
                    fontSize: 8,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    padding: "3px 10px",
                    fontWeight: 600,
                }}
            >
                Most Chosen
            </span>
        )}
        <p
            style={{
                color: GOLD,
                fontSize: 9,
                letterSpacing: "0.32em",
                textTransform: "uppercase",
                margin: 0,
                fontWeight: 500,
            }}
        >
            {tier}
        </p>
        <p
            className="font-serif"
            style={{
                color: "#fff",
                fontSize: 22,
                fontWeight: 400,
                margin: 0,
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
            }}
        >
            AED {price}
            <span
                style={{
                    fontSize: 11,
                    color: "#888",
                    fontFamily: "Inter, sans-serif",
                    letterSpacing: "0.05em",
                    marginLeft: 4,
                }}
            >
                /{period}
            </span>
        </p>
        <p
            style={{
                color: "#bbb",
                fontSize: 9.5,
                margin: 0,
                lineHeight: 1.5,
                fontWeight: 300,
            }}
        >
            {headline}
        </p>
        <div
            style={{
                borderTop: `1px solid ${GOLD_LINE_SOFT}`,
                marginTop: 2,
                paddingTop: 8,
            }}
        >
            {items.map((it, i) => (
                <p
                    key={i}
                    style={{
                        color: "#fff",
                        fontSize: 9.5,
                        margin: "3px 0",
                        lineHeight: 1.4,
                    }}
                >
                    · {it}
                </p>
            ))}
        </div>
    </div>
);

const BrochureRealEstate = () => {
    const reWa = waUrl(
        "Hi, I'd like to book a real estate professional reset session at The Reset Sanctuary.",
    );

    return (
        <main
            data-testid="brochure-realestate-page"
            style={{
                backgroundColor: "#0a0a0a",
                minHeight: "100vh",
                padding: "40px 16px 80px",
                color: "#bbb",
            }}
        >
            {/* Screen-only print button */}
            <div
                className="brochure-screen-only"
                style={{
                    maxWidth: 794,
                    margin: "0 auto 24px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 16,
                    flexWrap: "wrap",
                }}
            >
                <p
                    style={{
                        color: "#888",
                        fontSize: 12,
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        margin: 0,
                    }}
                >
                    Real Estate Brochure — A4 · 2 pages
                </p>
                <button
                    onClick={() => window.print()}
                    className="btn-gold-solid"
                    data-testid="brochure-re-print-btn"
                    style={{
                        cursor: "pointer",
                        fontFamily: "Inter, sans-serif",
                    }}
                >
                    Save as PDF / Print
                </button>
            </div>

            {/* ============== PAGE 1 ============== */}
            <PageFrame testid="brochure-re-a4">
                <PageHeader kicker="Sound Recovery for Real Estate" />

                {/* HERO */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 12,
                    }}
                >
                    <p
                        style={{
                            color: GOLD,
                            fontSize: 9,
                            letterSpacing: "0.35em",
                            textTransform: "uppercase",
                            margin: "0 0 8px",
                            fontWeight: 500,
                        }}
                    >
                        Sound Recovery for Real Estate Professionals
                    </p>
                    <h1
                        className="font-serif"
                        style={{
                            color: "#fff",
                            fontSize: 32,
                            fontWeight: 400,
                            lineHeight: 1.04,
                            letterSpacing: "-0.025em",
                            margin: "0 0 8px",
                            textTransform: "uppercase",
                        }}
                    >
                        Real Estate. Real Pressure.{" "}
                        <span style={{ color: GOLD }}>Real Recovery.</span>
                    </h1>
                </section>

                {/* HERO IMAGE */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        width: "100%",
                        aspectRatio: "16 / 9",
                        overflow: "hidden",
                        border: `1px solid ${GOLD_LINE}`,
                    }}
                    data-testid="re-hero-image"
                >
                    <img
                        src="/brochure-assets/realestate-pro.jpg"
                        alt="Real estate professional in luxury Dubai office"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center 35%",
                            display: "block",
                        }}
                    />
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            background:
                                "linear-gradient(180deg, rgba(10,10,10,0) 55%, rgba(10,10,10,0.7) 100%)",
                            pointerEvents: "none",
                        }}
                    />
                </section>

                {/* PROBLEM + SOLUTION + SHIFT STRIP */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                    }}
                >
                    <div data-testid="re-problem">
                        <SectionTitle
                            kicker="The Problem"
                            title="The mind that never switches off."
                        />
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                lineHeight: 1.6,
                                margin: 0,
                                fontWeight: 300,
                            }}
                        >
                            Deals, clients, deadlines, expectations. High
                            pressure, high stress, high stakes. The
                            top-performers in this market carry it 24/7 — and
                            it shows up in their sleep, focus, and decisions.
                        </p>
                    </div>
                    <div data-testid="re-solution">
                        <SectionTitle
                            kicker="The Solution"
                            title="Precision Frequency Recovery."
                            color={GOLD}
                        />
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                lineHeight: 1.6,
                                margin: 0,
                                fontWeight: 300,
                            }}
                        >
                            45 minutes. Deep reset. Sleep better, think clearer,
                            close more deals. A protocol built for the people
                            who can&apos;t afford to be off their game.
                        </p>
                    </div>
                </section>

                {/* SHIFT STRIP */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        padding: "12px 18px",
                        border: `1px solid ${GOLD_LINE}`,
                        display: "grid",
                        gridTemplateColumns: "1fr auto 1fr",
                        gap: 18,
                        alignItems: "center",
                    }}
                    data-testid="re-shift-strip"
                >
                    <div style={{ textAlign: "right" }}>
                        <p
                            style={{
                                color: "#777",
                                fontSize: 8,
                                letterSpacing: "0.3em",
                                textTransform: "uppercase",
                                margin: "0 0 6px",
                            }}
                        >
                            From
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                letterSpacing: "0.18em",
                                textTransform: "uppercase",
                                margin: 0,
                                lineHeight: 1.6,
                                textDecoration: "line-through",
                                textDecorationColor: "rgba(201,162,39,0.5)",
                            }}
                        >
                            Deals · Clients · Deadlines · Expectations
                        </p>
                    </div>
                    <span
                        style={{
                            color: GOLD,
                            fontSize: 16,
                            fontWeight: 300,
                        }}
                    >
                        →
                    </span>
                    <div>
                        <p
                            style={{
                                color: GOLD,
                                fontSize: 8,
                                letterSpacing: "0.3em",
                                textTransform: "uppercase",
                                margin: "0 0 6px",
                            }}
                        >
                            To
                        </p>
                        <p
                            style={{
                                color: "#fff",
                                fontSize: 10,
                                letterSpacing: "0.18em",
                                textTransform: "uppercase",
                                margin: 0,
                                lineHeight: 1.6,
                                fontWeight: 500,
                            }}
                        >
                            Focus · Balance · Clarity · Growth
                        </p>
                    </div>
                </section>

                {/* 4 BENEFITS */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 12,
                    }}
                    data-testid="re-benefits"
                >
                    <SectionTitle
                        kicker="The Benefits"
                        title="What changes after one session."
                    />
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: 10,
                        }}
                    >
                        <BenefitTile
                            Icon={Brain}
                            label="Reduce Stress & Overwhelm"
                            body="Calms a busy mind and lowers cortisol caused by high pressure and demanding days."
                            testid="re-benefit-stress"
                        />
                        <BenefitTile
                            Icon={Target}
                            label="Improve Focus & Decision Making"
                            body="Enhances mental clarity so you can make confident decisions and stay sharp."
                            testid="re-benefit-focus"
                        />
                        <BenefitTile
                            Icon={BatteryCharging}
                            label="Restore Energy & Prevent Burnout"
                            body="Rebalances your nervous system and restores energy for long-term success."
                            testid="re-benefit-energy"
                        />
                        <BenefitTile
                            Icon={Handshake}
                            label="Enhance Client Connections"
                            body="Show up calm, present and confident to build stronger relationships and close more deals."
                            testid="re-benefit-clients"
                        />
                    </div>
                </section>

                <PageFooter
                    pageNum={1}
                    tagline="Reset Your Mind · Elevate Your Business"
                />
            </PageFrame>

            {/* ============== PAGE 2 ============== */}
            <PageFrame testid="brochure-re-a4-page2">
                <PageHeader kicker="Science · Packages · Contact" />

                {/* SUCCESS SUPPORT — 5 small tiles */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 16,
                    }}
                    data-testid="re-success"
                >
                    <SectionTitle
                        kicker="Sound Recovery Supports Your Success"
                        title="Five outcomes for the long game."
                    />
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(5, 1fr)",
                            gap: 10,
                        }}
                    >
                        <SmallBenefit
                            Icon={ShieldCheck}
                            label="Manage Stress"
                            body="Stay calm under pressure and handle challenges with ease."
                            testid="re-success-stress"
                        />
                        <SmallBenefit
                            Icon={Moon}
                            label="Better Sleep"
                            body="Improve sleep quality so you wake up refreshed and ready to perform."
                            testid="re-success-sleep"
                        />
                        <SmallBenefit
                            Icon={TrendingUp}
                            label="Boost Productivity"
                            body="More focus, more energy and better time management."
                            testid="re-success-productivity"
                        />
                        <SmallBenefit
                            Icon={Scale}
                            label="Emotional Balance"
                            body="Stay balanced through market ups and downs and keep emotions in check."
                            testid="re-success-balance"
                        />
                        <SmallBenefit
                            Icon={InfinityIcon}
                            label="Sustain Long-Term Success"
                            body="Support your mind and body so you can build a life and business that last."
                            testid="re-success-longterm"
                        />
                    </div>
                </section>

                {/* EQUIPMENT — photos + pillars */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                    }}
                    data-testid="re-equipment"
                >
                    <SectionTitle
                        kicker="Equipment & Frequencies"
                        title="The protocol toolkit."
                    />
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 12,
                            marginBottom: 10,
                        }}
                    >
                        {[
                            {
                                src: "/brochure-assets/pemf-mat.jpg",
                                label: "PEMF Mat",
                                caption:
                                    "Pulsed electromagnetic stimulation for cellular recovery and inflammation reduction.",
                                testid: "re-equipment-pemf",
                            },
                            {
                                src: "/brochure-assets/tuning-forks.jpg",
                                label: "Tuning Forks",
                                caption:
                                    "Calibrated to 432 · 528 · 174 · 741 Hz — the recovery and regulation bands.",
                                testid: "re-equipment-forks",
                            },
                        ].map((item) => (
                            <div
                                key={item.testid}
                                data-testid={item.testid}
                                style={{
                                    border: `1px solid ${GOLD_LINE}`,
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <div
                                    style={{
                                        width: "100%",
                                        aspectRatio: "16 / 9",
                                        overflow: "hidden",
                                    }}
                                >
                                    <img
                                        src={item.src}
                                        alt={item.label}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                        }}
                                    />
                                </div>
                                <div style={{ padding: "8px 12px 10px" }}>
                                    <p
                                        style={{
                                            color: GOLD,
                                            fontSize: 8.5,
                                            letterSpacing: "0.3em",
                                            textTransform: "uppercase",
                                            margin: "0 0 3px",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {item.label}
                                    </p>
                                    <p
                                        style={{
                                            color: "#bbb",
                                            fontSize: 9,
                                            lineHeight: 1.45,
                                            margin: 0,
                                            fontWeight: 300,
                                        }}
                                    >
                                        {item.caption}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: "2px 16px",
                        }}
                    >
                        <Pillar
                            title="Frequency Generator"
                            body="Precision frequencies that restore balance and optimise vitality."
                        />
                        <Pillar
                            title="Singing Bowls"
                            body="Harmonic resonance for autonomic downshift."
                        />
                    </div>
                </section>

                {/* PRACTITIONER */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        padding: "12px 14px",
                        border: `1px solid ${GOLD_LINE}`,
                        display: "grid",
                        gridTemplateColumns: "62px 1fr",
                        gap: 12,
                        alignItems: "center",
                    }}
                >
                    <img
                        src="/brochure-assets/tatva.png"
                        alt="Tatva K"
                        style={{
                            width: 62,
                            height: 62,
                            objectFit: "cover",
                            objectPosition: "center 25%",
                            borderRadius: "50%",
                            border: `1px solid rgba(201,162,39,0.4)`,
                            display: "block",
                        }}
                        data-testid="re-tatva-photo"
                    />
                    <div>
                        <p
                            style={{
                                color: GOLD,
                                fontSize: 9,
                                letterSpacing: "0.32em",
                                textTransform: "uppercase",
                                margin: "0 0 4px",
                                fontWeight: 500,
                            }}
                        >
                            Led by Tatva K
                        </p>
                        <p
                            style={{
                                color: "#fff",
                                fontSize: 9,
                                lineHeight: 1.5,
                                margin: 0,
                                fontWeight: 300,
                            }}
                        >
                            TEDx Speaker · 150M+ Streams · Reiki Grandmaster ·
                            Berklee College of Music — Music Therapy
                            Masterclass · ISTA Certified · IPHM Certified
                        </p>
                    </div>
                </section>

                {/* PACKAGES */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            marginBottom: 14,
                        }}
                    >
                        <h2
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 18,
                                fontWeight: 500,
                                margin: 0,
                                letterSpacing: "0.01em",
                            }}
                        >
                            Real Estate Packages
                        </h2>
                        <p
                            style={{
                                color: "#888",
                                fontSize: 9,
                                letterSpacing: "0.18em",
                                textTransform: "uppercase",
                                margin: 0,
                            }}
                        >
                            Private · Monthly · Team
                        </p>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: 14,
                        }}
                    >
                        <Package
                            tier="Individual"
                            price="7,999"
                            period="session"
                            headline="Full protocol, private, one-off reset."
                            items={[
                                "1 private session",
                                "45-min protocol",
                                "Personal intake",
                                "Post-session debrief",
                            ]}
                        />
                        <Package
                            tier="Monthly"
                            price="11,999"
                            period="month"
                            headline="Weekly reset. The high-performer's ritual."
                            featured
                            items={[
                                "4 sessions per month",
                                "Weekly cadence",
                                "Priority scheduling",
                                "Personal protocol design",
                            ]}
                        />
                        <Package
                            tier="Team"
                            price="14,999"
                            period="day"
                            headline="Reset the whole agency in one day."
                            items={[
                                "Up to 10 agents",
                                "Group session",
                                "On-site or in-villa",
                                "Founder/leadership debrief",
                            ]}
                        />
                    </div>
                    <p
                        style={{
                            color: "#777",
                            fontSize: 8.5,
                            margin: "10px 0 0",
                            letterSpacing: "0.04em",
                        }}
                    >
                        All prices in AED. Bespoke and ongoing retainer
                        engagements available on request.
                    </p>
                </section>

                {/* PAYMENT + CONTACT */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        display: "grid",
                        gridTemplateColumns: "1.1fr 1fr",
                        gap: 18,
                        marginBottom: 10,
                    }}
                >
                    <div>
                        <h2
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 20,
                                fontWeight: 400,
                                margin: "0 0 6px",
                                letterSpacing: "-0.01em",
                            }}
                        >
                            Reset Your Mind.{" "}
                            <span style={{ color: GOLD }}>
                                Elevate Your Business.
                            </span>
                        </h2>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                lineHeight: 1.55,
                                margin: 0,
                                maxWidth: 360,
                                fontWeight: 300,
                            }}
                        >
                            Reserve via WhatsApp. Same-day confirmation. Walk
                            in, drop the weight, walk out reset.
                        </p>
                        <div
                            style={{
                                marginTop: 12,
                                display: "flex",
                                flexDirection: "column",
                                gap: 6,
                            }}
                            data-testid="re-payments"
                        >
                            <p
                                style={{
                                    color: GOLD,
                                    fontSize: 8,
                                    letterSpacing: "0.3em",
                                    textTransform: "uppercase",
                                    margin: 0,
                                    fontWeight: 500,
                                }}
                            >
                                Flexible Payment Options
                            </p>
                            <div
                                style={{
                                    display: "flex",
                                    gap: 10,
                                    flexWrap: "wrap",
                                }}
                            >
                                <span
                                    style={{
                                        padding: "8px 14px",
                                        border: `1px solid ${GOLD_LINE}`,
                                        color: "#fff",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 10,
                                        backgroundColor:
                                            "rgba(255,255,255,0.02)",
                                    }}
                                    data-testid="re-tabby"
                                >
                                    <img
                                        src="/brochure-assets/tabby-logo.png"
                                        alt="tabby"
                                        style={{
                                            height: 16,
                                            width: "auto",
                                            display: "block",
                                        }}
                                    />
                                    <span
                                        style={{
                                            color: "#bbb",
                                            fontSize: 8.5,
                                            letterSpacing: "0.16em",
                                            textTransform: "uppercase",
                                            borderLeft: `1px solid rgba(201,162,39,0.3)`,
                                            paddingLeft: 10,
                                        }}
                                    >
                                        4 interest-free payments
                                    </span>
                                </span>
                                <span
                                    style={{
                                        padding: "8px 14px",
                                        border: `1px solid ${GOLD_LINE}`,
                                        color: "#fff",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 10,
                                        backgroundColor:
                                            "rgba(255,255,255,0.02)",
                                    }}
                                    data-testid="re-tamara"
                                >
                                    <img
                                        src="/brochure-assets/tamara-logo.png"
                                        alt="tamara"
                                        style={{
                                            height: 16,
                                            width: "auto",
                                            display: "block",
                                        }}
                                    />
                                    <span
                                        style={{
                                            color: "#bbb",
                                            fontSize: 8.5,
                                            letterSpacing: "0.16em",
                                            textTransform: "uppercase",
                                            borderLeft: `1px solid rgba(201,162,39,0.3)`,
                                            paddingLeft: 10,
                                        }}
                                    >
                                        Split your payment
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            textAlign: "right",
                            fontFamily: "Inter, sans-serif",
                        }}
                    >
                        <p
                            style={{
                                color: GOLD,
                                fontSize: 9,
                                letterSpacing: "0.3em",
                                textTransform: "uppercase",
                                margin: "0 0 6px",
                                fontWeight: 500,
                            }}
                        >
                            Reserve via WhatsApp
                        </p>
                        <p
                            className="font-serif"
                            style={{
                                color: "#fff",
                                fontSize: 18,
                                fontWeight: 500,
                                margin: "0 0 6px",
                                letterSpacing: "0.02em",
                            }}
                        >
                            {PHONE_DISPLAY}
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                margin: "0 0 2px",
                            }}
                        >
                            {EMAIL}
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                margin: "0 0 2px",
                            }}
                        >
                            theresetsanctuary.org
                        </p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                margin: "0 0 10px",
                            }}
                        >
                            Instagram {INSTAGRAM}
                        </p>
                        <a
                            href={reWa}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="re-cta"
                            className="brochure-screen-only"
                            style={{
                                display: "inline-block",
                                color: "#0a0a0a",
                                backgroundColor: GOLD,
                                padding: "10px 18px",
                                fontSize: 11,
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                                fontWeight: 600,
                                textDecoration: "none",
                            }}
                        >
                            Reset Your Mind
                        </a>
                    </div>
                </section>

                {/* DISCLAIMER */}
                <p
                    style={{
                        color: "#666",
                        fontSize: 7,
                        lineHeight: 1.5,
                        textAlign: "center",
                        margin: "0 0 10px",
                        position: "relative",
                        zIndex: 1,
                        paddingTop: 8,
                        borderTop: `1px solid ${GOLD_LINE_SOFT}`,
                    }}
                >
                    The Reset Sanctuary offers wellness and recovery sessions
                    through precision sound and frequency work. We do not
                    diagnose, treat, or cure medical conditions. Our services
                    are complementary wellness practices, not medical care.
                    Please consult a licensed healthcare provider for medical
                    concerns. © 2026 Tatva K.
                </p>

                <PageFooter
                    pageNum={2}
                    tagline="Clear Mind · Strong Energy · Limitless Possibilities"
                />
            </PageFrame>
        </main>
    );
};

export default BrochureRealEstate;
